import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { publicFileAttachmentsMaybe } from '../../ducks/messageAttachments.duck';
import { fetchSkillsAndTools } from '../../ducks/SkillsAndTools.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  buildProfileSchema,
  knowsAboutFromPublicData,
  serviceListingSchemaData,
  portfolioWorkSchemaData,
} from './profileSchema';
import { trackIntercomEvent } from '../../util/intercom';
import { richTextToMetaDescription } from '../../util/seo';
import { orderPortfolioWorks } from '../../util/portfolioOrder';

import { TRANSITION_ENQUIRE } from '../../util/transaction';

import { util as sdkUtil } from '../../util/sdkLoader';

import {
  fetchProfileUserName,
  fetchOffPlatformTestimonials,
  fetchOffPlatformReviews,
  markTxUnread,
} from '../../util/api';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST';
export const QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS';
export const QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR =
  'app/ProfilePage/QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR';

export const QUERY_OFF_PLATFORM_REVIEWS_REQUEST =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_REQUEST';
export const QUERY_OFF_PLATFORM_REVIEWS_SUCCESS =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_SUCCESS';
export const QUERY_OFF_PLATFORM_REVIEWS_ERROR = 'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_ERROR';

export const QUERY_PROFILE_USER_NAME_REQUEST = 'app/ProfilePage/QUERY_PROFILE_USER_NAME_REQUEST';
export const QUERY_PROFILE_USER_NAME_SUCCESS = 'app/ProfilePage/QUERY_PROFILE_USER_NAME_SUCCESS';
export const QUERY_PROFILE_USER_NAME_ERROR = 'app/ProfilePage/QUERY_PROFILE_USER_NAME_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ProfilePage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ProfilePage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ProfilePage/SEND_ENQUIRY_ERROR';

export const SET_PROFILE_SCHEMA = 'app/ProfilePage/SET_PROFILE_SCHEMA';
export const PROFILE_DATA_LOADED = 'app/ProfilePage/PROFILE_DATA_LOADED';
export const SET_FAVORITE_PROFILES = 'app/ProfilePage/SET_FAVORITE_PROFILES';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  offPlatformReviews: [],
  queryOffPlatformReviewsError: null,
  offPlatformTestimonials: [],
  queryOffPlatformTestimonialsError: null,
  profileUserName: null,
  profileUserNameError: null,
  profileSchema: null,
  favoriteProfiles: [],
  // True once loadData has finished, so that every section is in place (the
  // page scrolls to a section named in the URL hash only then).
  profileDataLoaded: false,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };

    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    case QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST:
      return { ...state, queryOffPlatformTestimonialsError: null };
    case QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS:
      return { ...state, offPlatformTestimonials: payload };
    case QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR:
      return { ...state, offPlatformTestimonials: [], queryOffPlatformTestimonialsError: payload };

    case QUERY_OFF_PLATFORM_REVIEWS_REQUEST:
      return { ...state, queryOffPlatformReviewsError: null };
    case QUERY_OFF_PLATFORM_REVIEWS_SUCCESS:
      return { ...state, offPlatformReviews: payload };
    case QUERY_OFF_PLATFORM_REVIEWS_ERROR:
      return { ...state, offPlatformReviews: [], queryOffPlatformReviewsError: payload };

    case QUERY_PROFILE_USER_NAME_REQUEST:
      return { ...state, profileUserName: null };
    case QUERY_PROFILE_USER_NAME_SUCCESS:
      return { ...state, profileUserName: payload };
    case QUERY_PROFILE_USER_NAME_ERROR:
      return { ...state, profileUserNameError: payload };
    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryError: null };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryError: payload };

    case SET_PROFILE_SCHEMA:
      return { ...state, profileSchema: payload };
    case PROFILE_DATA_LOADED:
      return { ...state, profileDataLoaded: true };

    case SET_FAVORITE_PROFILES:
      return { ...state, favoriteProfiles: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const queryOffPlatformTestimonialsRequest = () => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_REQUEST,
});

export const queryOffPlatformTestimonialsSuccess = testimonials => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_SUCCESS,
  payload: testimonials,
});

export const queryOffPlatformTestimonialsError = e => ({
  type: QUERY_OFF_PLATFORM_TESTIMONIALS_ERROR,
  error: true,
  payload: e,
});

export const queryOffPlatformReviewsRequest = () => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_REQUEST,
});

export const queryOffPlatformReviewsSuccess = reviews => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryOffPlatformReviewsError = e => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const queryProfileUserNameRequest = () => ({
  type: QUERY_PROFILE_USER_NAME_REQUEST,
});

export const queryProfileUserNameSuccess = profileUserName => ({
  type: QUERY_PROFILE_USER_NAME_SUCCESS,
  payload: profileUserName,
});

export const queryProfileUserNameError = e => ({
  type: QUERY_PROFILE_USER_NAME_ERROR,
  error: true,
  payload: e,
});

export const sendEnquiryRequest = () => ({
  type: SEND_ENQUIRY_REQUEST,
});
export const sendEnquirySuccess = () => ({
  type: SEND_ENQUIRY_SUCCESS,
});
export const sendEnquiryError = e => ({
  type: SEND_ENQUIRY_ERROR,
  error: true,
  payload: e,
});

export const setProfileSchema = schema => ({
  type: SET_PROFILE_SCHEMA,
  payload: schema,
});

export const setProfileDataLoaded = () => ({
  type: PROFILE_DATA_LOADED,
});

export const setFavoriteProfiles = favoriteProfiles => ({
  type: SET_FAVORITE_PROFILES,
  payload: favoriteProfiles,
});

// ================ Thunks ================ //

export const queryUserListings = userId => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));
  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'fields.image': [
        'variants.landscape-crop',
        'variants.landscape-crop2x',
        'variants.vertical',
        'variants.horizontal',
      ],
      'imageVariant.horizontal': sdkUtil.objectQueryString({
        w: 1920,
        h: 1080,
        fit: 'scale',
      }),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const showUser = userId => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess());

      const user = response.data.data;

      if (typeof window === 'object') {
        window.dataLayer.push({
          event: 'view_item',
          content_type: 'freelancer',
          ecommerce: {
            items: [
              {
                item_name: user.attributes.profile.displayName,
                item_id: user.id.uuid,
                quantity: '1',
              },
            ],
          },
        });
      }
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const queryProfileUserName = userId => (dispatch, getState, sdk) => {
  dispatch(queryProfileUserNameRequest());

  return fetchProfileUserName(userId)
    .then(response => {
      dispatch(queryProfileUserNameSuccess(response.fullName));
    })
    .catch(e => dispatch(queryProfileUserNameError(storableError(e))));
};

export const queryOffPlatformTestimonials = userId => (dispatch, getState, sdk) => {
  dispatch(queryOffPlatformTestimonialsRequest());

  return fetchOffPlatformTestimonials(userId.uuid)
    .then(response => {
      dispatch(queryOffPlatformTestimonialsSuccess(response.items));
    })
    .catch(e => dispatch(queryOffPlatformTestimonialsError(storableError(e))));
};

export const queryOffPlatformReviews = userId => (dispatch, getState, sdk) => {
  dispatch(queryOffPlatformReviewsRequest());

  return fetchOffPlatformReviews(userId.uuid)
    .then(response => {
      dispatch(queryOffPlatformReviewsSuccess(response.items));
    })
    .catch(e => dispatch(queryOffPlatformReviewsError(storableError(e))));
};

export const sendProfileEnquiry = (
  profileListingId,
  isLightEntrepreneur,
  message,
  currentUser,
  whoAmI,
  fileIds = []
) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());

  // Every enquiry starts on default or light; invoicing is a branch inside
  // those processes (spec 2026-09-22), so the customer's flags play no part.
  const processAlias = isLightEntrepreneur ? config.lightProcessAlias : config.bookingProcessAlias;

  const bodyParams = {
    transition: TRANSITION_ENQUIRE,
    processAlias: processAlias,
    params: { listingId: profileListingId, protectedData: { listingTitle: whoAmI } },
  };

  return sdk.transactions
    .initiate(bodyParams, { expand: true, include: ['provider'] })
    .then(response => {
      const transaction = response.data.data;
      const transactionId = transaction.id;
      const provider = transaction.relationships.provider.data;
      const recipientId = provider.id;

      return sdk.messages
        .send(
          { transactionId, content: message, ...publicFileAttachmentsMaybe(fileIds) },
          { expand: true }
        )
        .then(res => {
          const messageData = res.data.data;
          // Send the message to the created transaction
          return markTxUnread({
            transactionId: transactionId.uuid,
            recipientId: recipientId.uuid,
            message: messageData,
          }).then(() => {
            dispatch(sendEnquirySuccess());
            dispatch(fetchCurrentUserHasOrdersSuccess(true));
            return { response, recipientId };
          });
        });
    })
    .then(({ response, recipientId }) => {
      if (typeof window === 'object') {
        trackIntercomEvent('New profile enquiry', {
          transactionId: response.data.data.id.uuid,
          providerId: recipientId.uuid,
          customerId: currentUser.id.uuid,
          transition: 'TRANSITION_ENQUIRE',
          listingType: 'profile',
          processName: isLightEntrepreneur ? 'flex-light-process' : 'flex-default-process',
        });
      }
      return response;
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const loadData = userId => async (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  dispatch(setInitialState());

  const state = getState();
  const skills = state.SkillsAndTools.skillOptions;
  const tools = state.SkillsAndTools.toolOptions;

  const fetchSkillsAndToolsIfNeeded = () => {
    if (skills.length === 0 || tools.length === 0) {
      return dispatch(fetchSkillsAndTools());
    }
    return Promise.resolve();
  };

  // Started before the user data so the lists usually arrive first, also on
  // client-side navigation; awaited below before the schema needs them. The
  // catch keeps an early failure below from leaving it unhandled (the fetch
  // itself never rejects).
  const skillsAndToolsPromise = fetchSkillsAndToolsIfNeeded().catch(() => {});

  try {
    // Enforce sequential execution
    await dispatch(fetchCurrentUser());

    // Load favorite profiles from current user to prevent hydration mismatches
    const stateAfterUser = getState();
    const currentUser = stateAfterUser.user.currentUser;
    if (currentUser?.id) {
      const favoriteProfiles = currentUser.attributes?.profile?.privateData?.favoriteProfiles || [];
      dispatch(setFavoriteProfiles(favoriteProfiles));
    }

    await dispatch(showUser(userId));
    await dispatch(queryUserListings(userId));
    await dispatch(queryUserReviews(userId));
    await dispatch(queryOffPlatformTestimonials(userId));
    await dispatch(queryOffPlatformReviews(userId));
    await skillsAndToolsPromise;
    // Uncomment if needed
    // await dispatch(queryProfileUserName(userId.uuid));

    // Build schema server-side after all data is loaded
    const updatedState = getState();
    const user = updatedState.marketplaceData.entities.user?.[userId.uuid];
    const listings = updatedState.ProfilePage.userListingRefs
      .map(ref => updatedState.marketplaceData.entities.listing?.[ref.id.uuid])
      .filter(Boolean);
    const reviews = updatedState.ProfilePage.reviews;
    const offPlatformReviews = updatedState.ProfilePage.offPlatformReviews || [];
    const offPlatformTestimonials = updatedState.ProfilePage.offPlatformTestimonials || [];

    if (user) {
      // Combine reviews (on-platform + off-platform)
      const formattedReviews = reviews.map(review => ({
        content: review?.attributes?.content,
        rating: review?.attributes?.rating,
        createdAt: review?.attributes?.createdAt,
        authorName: review?.author?.attributes?.profile?.displayName,
      }));

      const formattedOffPlatformReviews = offPlatformReviews.map(offReview => ({
        content: offReview?.content,
        rating: offReview?.reviewRating,
        createdAt: new Date(offReview?.createdAt),
        authorName: offReview?.displayName,
      }));

      const combinedReviews = [...formattedReviews, ...formattedOffPlatformReviews];

      // Extract user profile data
      const displayName = user?.attributes?.profile?.displayName;
      const whoAmI = user?.attributes?.profile?.publicData?.whoAmI;
      // Read the bio from publicData: `user` comes from marketplaceData, so profile.bio has
      // already been through sanitizeText() which rewrites <> to fullwidth ＜＞.
      const bio = richTextToMetaDescription(user?.attributes?.profile?.publicData?.bio);
      const locationCity = user?.attributes?.profile?.publicData?.locationCity;
      const locationCountryCode = user?.attributes?.profile?.publicData?.locationCountryCode;
      const canonicalRootURL = process.env.REACT_APP_CANONICAL_ROOT_URL;
      const profileUrl = `${canonicalRootURL}/u/${userId.uuid}`;

      // Filter and sort portfolio works
      const portfolioWorks = orderPortfolioWorks(
        listings,
        user?.attributes?.profile?.publicData?.featuredPortfolioOrder
      );

      // Raw marketplaceData entities are not denormalised, so image urls are
      // resolved through relationships + the image entity map.
      const imageEntities = updatedState.marketplaceData.entities.image || {};
      const workImageUrl = work => {
        const imageId = work?.relationships?.images?.data?.[0]?.id?.uuid;
        const variants = imageId ? imageEntities[imageId]?.attributes?.variants : null;
        return variants?.['landscape-crop2x']?.url || variants?.horizontal?.url;
      };
      const profileImageId = user?.relationships?.profileImage?.data?.id?.uuid;
      const profileImageUrl = profileImageId
        ? imageEntities[profileImageId]?.attributes?.variants?.['square-small2x']?.url
        : undefined;

      const services = listings
        .filter(
          l =>
            l?.attributes?.publicData?.listingType === 'service' &&
            !l?.attributes?.publicData?.hidden
        )
        .map(listing => serviceListingSchemaData(listing, canonicalRootURL));

      const works = portfolioWorks.map(work =>
        portfolioWorkSchemaData(work, canonicalRootURL, workImageUrl(work))
      );

      const knowsAbout = knowsAboutFromPublicData(
        user?.attributes?.profile?.publicData,
        updatedState.SkillsAndTools.skillOptions,
        updatedState.SkillsAndTools.toolOptions,
        config.locale
      );

      const schema = buildProfileSchema({
        profileUrl,
        schemaTitle: `${displayName} | ${whoAmI || 'Freelancer'}`,
        displayName,
        whoAmI,
        knowsAbout,
        bio,
        locationCity,
        locationCountryCode,
        profileImageUrl,
        createdAt: user?.attributes?.createdAt,
        reviews: combinedReviews,
        services,
        works,
      });

      // Store the precomputed schema in Redux state
      dispatch(setProfileSchema(schema));
    }
  } catch (error) {
    console.error('Error loading data:', error);
  }
  dispatch(setProfileDataLoaded());
};
